import { viewTabHandle } from "@yjsgames/phaser-ui/tab-registry";
import {
  SPACESHIP_DEFS,
  moduleGridStateForSpaceship,
  spaceshipById,
  type SpaceshipDefinition,
} from "../data/spaceshipData.js";
import { canUnlockSpaceship } from "../data/spaceshipUnlock.js";
import { SpaceshipTabView, type SpaceshipTabData } from "../ui/SpaceshipTabView.js";
import { openLauncherInfoModal } from "../ui/moduleInfoModal.js";
import type { DomainBootstrapper } from "./BootTypes.js";
import type { GameBoot } from "./BootTypes.js";

function buildSpaceshipTabData(currentId: string, ownedIds: readonly string[], bestChapter: number): SpaceshipTabData {
  return {
    spaceships: SPACESHIP_DEFS as readonly SpaceshipDefinition[],
    currentId,
    ownedIds,
    unlockableIds: SPACESHIP_DEFS.filter((def) => canUnlockSpaceship(def, bestChapter)).map((def) => def.id),
  };
}

function equipSpaceship(boot: GameBoot, id: string): boolean {
  const def = spaceshipById(id);
  if (!def) return false;
  const spaceship = boot.ctx.spaceship();
  spaceship.setModuleUnlockedCells(spaceship.selectedId, boot.ctx.grid().toState().unlockedCells);
  if (!spaceship.owns(id)) {
    if (!canUnlockSpaceship(def, boot.ctx.chapter().bestChapter)) return false;
    spaceship.unlockSpaceship(id);
  }
  if (!spaceship.select(id)) {
    boot.ports.invalidateUi();
    boot.ports.persistNow();
    return false;
  }
  boot.ctx.grid().resetToState(moduleGridStateForSpaceship(def, spaceship.moduleUnlockedCells(id)));
  boot.ports.resetFireTimer();
  boot.ports.applySpaceshipVisualScale?.();
  boot.ports.invalidateUi();
  boot.ports.persistNow();
  return true;
}

function openSpaceshipInfo(boot: GameBoot, id: string): void {
  const def = spaceshipById(id);
  if (!def) return;
  const screen = boot.screen();
  const spaceship = boot.ctx.spaceship();
  const infoDeps = {
    modals: boot.modals,
    ctx: boot.ctx,
    screen: () => boot.screen(),
  };
  const buildData = () => ({
    spaceship: def,
    current: spaceship.selectedId === id,
    owned: spaceship.owns(id),
    unlockable: canUnlockSpaceship(def, boot.ctx.chapter().bestChapter),
    unlockedCells: spaceship.moduleUnlockedCells(id),
  });
  boot.modals.openSpaceshipInfo({
    screenWidth: screen.width,
    screenHeight: screen.height,
    onEquip: (equipId) => {
      if (equipSpaceship(boot, equipId)) {
        boot.modals.closeSpaceshipInfo();
      } else {
        boot.modals.updateSpaceshipInfo(buildData());
      }
    },
    onClose: () => boot.modals.closeSpaceshipInfo(),
    onLauncherInfo: () => openLauncherInfoModal(infoDeps, def),
  });
  boot.modals.updateSpaceshipInfo(buildData());
}

export const spaceshipBoot: DomainBootstrapper = {
  key: "spaceship",
  install(boot) {
    boot.tabs.registerLazy("spaceship", {
      mount: () => {
        const { tabContent } = boot.layout;
        const view = new SpaceshipTabView(
          boot.scene,
          tabContent.x,
          tabContent.y,
          tabContent.width,
          boot.layout.shell.tabPanelBottom,
          {
            onInfo: (id) => openSpaceshipInfo(boot, id),
            onEquip: (id) => equipSpaceship(boot, id),
          },
        );
        return viewTabHandle(
          view,
          () => view.update(buildSpaceshipTabData(boot.ctx.spaceship().selectedId, boot.ctx.spaceship().ownedIds(), boot.ctx.chapter().bestChapter)),
          () => `${boot.ctx.spaceship().selectedId}#${boot.ctx.spaceship().ownedIds().join(",")}#${boot.ctx.chapter().bestChapter}#${SPACESHIP_DEFS.map((def) => def.id).join(",")}`,
        );
      },
    });
  },
};
